import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersPage from "@/pages/OrdersPage";
import CreateOrderPage from "@/pages/CreateOrderPage";
import OrderDetailsPage from "@/pages/OrderDetailsPage";
import ClientsPage from "@/pages/ClientsPage";
import StaffPage from "@/pages/StaffPage";
import ReportsPage from "@/pages/ReportsPage";
import BillingPage from "@/pages/BillingPage";
import BillingClientPage from "@/pages/BillingClientPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import { Loader2 } from "lucide-react";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="orders" element={<RequireRole roles={["admin", "staff"]}><OrdersPage /></RequireRole>} />
              <Route path="my-orders" element={<RequireRole roles={["client"]}><OrdersPage /></RequireRole>} />
              <Route path="orders/new" element={<RequireRole roles={["admin", "staff"]}><CreateOrderPage /></RequireRole>} />
              <Route path="orders/:id" element={<OrderDetailsPage />} />
              <Route path="clients" element={<RequireRole roles={["admin"]}><ClientsPage /></RequireRole>} />
              <Route path="staff" element={<RequireRole roles={["admin"]}><StaffPage /></RequireRole>} />
              <Route path="reports" element={<RequireRole roles={["admin"]}><ReportsPage /></RequireRole>} />
              <Route path="billing" element={<RequireRole roles={["admin", "client"]}><BillingPage /></RequireRole>} />
              <Route path="billing/:clientId" element={<RequireRole roles={["admin"]}><BillingClientPage /></RequireRole>} />
              <Route path="audit-logs" element={<RequireRole roles={["admin"]}><AuditLogsPage /></RequireRole>} />
              <Route path="settings" element={<RequireRole roles={["admin"]}><SettingsPage /></RequireRole>} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
